import {
  Box,
  Flex,
  Slider,
  SliderFilledTrack,
  SliderThumb,
  SliderTrack,
  Tooltip,
} from '@chakra-ui/react';
import _ from 'lodash';
import React, { ChangeEvent, useState } from 'react';

import {
  CurrencyInput,
  Input,
  PercentageInput,
} from 'packages/common-frontend/components/Inputs';
import { colors } from 'packages/common-frontend/constants';

// ===== typeDefs  =====
type AdjustmentInputDisplayCallbackFn = (val: number) => string | number;
type AdjustmentSliderLabelDisplayCallbackFn = (
  val: number,
  labelType: string,
) => string | undefined;
export type AdjustmentSliderInputPosition = 'left' | 'right' | undefined;
export type AdjustmentSliderInputType =
  | 'currency'
  | 'fraction'
  | 'percentage'
  | undefined;

// ===== Helpers =====
const inputDisplayCallbackPassThrough = (value: number) => value;
// TODO: Check to see if we can use more strict type
const inputMap = new Map<AdjustmentSliderInputType, any>([
  ['currency', CurrencyInput],
  ['percentage', PercentageInput],
  [undefined, Input],
]);

/**
 * Slider
 */
const WrappedSlider = ({
  value,
  setValue,
  max = 100,
  min = 0,
  step = 1,
  labelDisplayCallback,
  showTooltip,
  tooltipLabel,
}: {
  value: number;
  setValue: Function;
  max?: number;
  min?: number;
  step?: number;
  labelDisplayCallback?: AdjustmentSliderLabelDisplayCallbackFn;
  showTooltip?: boolean;
  tooltipLabel?: string;
}) => {
  // // Convert display based on type
  const minLabel = labelDisplayCallback
    ? labelDisplayCallback(min, 'MIN')
    : min;
  const maxLabel = labelDisplayCallback
    ? labelDisplayCallback(max, 'MAX')
    : max;

  return (
    <Box>
      <Flex justifyContent="space-between" alignItems="end">
        <Box fontSize="sm">{minLabel}</Box>
        <Box marginLeft="auto" textAlign="right" fontSize="sm">
          {maxLabel}
        </Box>
      </Flex>

      <Flex>
        <Slider
          value={value}
          onChange={(val) => setValue(val)}
          max={max}
          min={min}
          step={step}
        >
          <SliderTrack>
            <SliderFilledTrack bg={colors.fundingRequest.sliderGreen} />
          </SliderTrack>
          <Tooltip
            hasArrow
            bg="gray.200"
            color="black"
            placement="bottom"
            isOpen={showTooltip}
            label={tooltipLabel}
            textAlign="center"
            whiteSpace="pre-line"
          >
            <SliderThumb />
          </Tooltip>
        </Slider>
      </Flex>
    </Box>
  );
};

WrappedSlider.defaultProps = {
  max: 100,
  min: 1,
  step: 1,
  labelDisplayCallback: undefined,
  showTooltip: false,
  tooltipLabel: undefined,
};

export type Constraints = { max: number; min: number; step: number };

export interface AdjustmentSliderParams {
  value: number;
  setValue: Function;
  constraints: Constraints;
  inputPosition?: AdjustmentSliderInputPosition;
  inputType?: AdjustmentSliderInputType;
  inputDisplayCallback?: AdjustmentInputDisplayCallbackFn;
  labelDisplayCallback?: AdjustmentSliderLabelDisplayCallbackFn;
  showTooltip?: boolean;
  tooltipLabel?: string;
}

/**
 * Component for funding request adjustment text-slider-input combo
 */
export const AdjustmentSlider = ({
  value,
  setValue: setGlobalValue,
  constraints,
  inputPosition,
  inputType,
  inputDisplayCallback = inputDisplayCallbackPassThrough,
  labelDisplayCallback,
  showTooltip,
  tooltipLabel,
}: AdjustmentSliderParams) => {
  // We will keep localState.value as string, so that user can enter `100.` as intermediate step.  This will be casted back to Number
  // as user exit out of EditMode
  const [localState, setLocalState] = useState({
    inEditMode: false,
    value: '',
  });

  // ===== Handlers =====
  // Exit out of EditMode
  const handleInputOnBlur = () => {
    setGlobalValue(Number(localState.value));
    setLocalState({
      inEditMode: false,
      value: '',
    });
  };
  // This method handles value change event from <input> element
  const handleInputOnChange = (event: ChangeEvent<HTMLInputElement>) => {
    setLocalState({
      ...localState,
      value: event.target.value,
    });
  };
  // Enter Edit Mode
  const handleInputOnFocus = () => {
    setLocalState({
      inEditMode: true,
      value: value.toString(),
    });
  };

  const max = _.get(constraints, 'max');
  const min = _.get(constraints, 'min');
  const step = _.get(constraints, 'step');

  const InputComponent = inputMap.get(inputType);
  const inputDisplayValue = localState.inEditMode
    ? localState.value
    : inputDisplayCallback(value);
  let inputComponentSpaceProp = {};
  if (inputType === 'currency') {
    inputComponentSpaceProp = { postfixSpace: true };
  } else if (inputType === 'percentage') {
    inputComponentSpaceProp = { prefixSpace: true };
  }

  const inputComponentProps = {
    w: '150px',
    variant: 'flushed',
    value: inputDisplayValue,
    onBlur: handleInputOnBlur,
    onChange: handleInputOnChange,
    onFocus: handleInputOnFocus,
    ...inputComponentSpaceProp,
    // postfixSpace: true,
    // prefixSpace: true,
  };

  return (
    <Flex>
      {inputPosition === 'left' && (
        <Flex alignItems="center" mr="25px">
          <InputComponent {...inputComponentProps} />
        </Flex>
      )}
      <Box flex="1" minW="200px" m={6}>
        <WrappedSlider
          max={max}
          min={min}
          step={step}
          value={value}
          setValue={setGlobalValue}
          labelDisplayCallback={labelDisplayCallback}
          showTooltip={showTooltip}
          tooltipLabel={tooltipLabel}
        />
      </Box>
      {inputPosition === 'right' && (
        <Flex alignItems="center" ml="25px">
          <InputComponent {...inputComponentProps} />
        </Flex>
      )}
    </Flex>
  );
};

AdjustmentSlider.defaultProps = {
  inputPosition: undefined,
  inputType: undefined,
  inputDisplayCallback: inputDisplayCallbackPassThrough,
  labelDisplayCallback: undefined,
  showTooltip: false,
  tooltipLabel: undefined,
};
